import { DEG_TO_RAD, RAD_TO_DEG } from "./constants";
import type { Vector3Data } from "../types/astronomy";

export function degToRad(degrees: number): number {
  return degrees * DEG_TO_RAD;
}

export function radToDeg(radians: number): number {
  return radians * RAD_TO_DEG;
}

export function normalizeAngleRad(angle: number): number {
  const twoPi = Math.PI * 2;
  let result = angle % twoPi;
  if (result < 0) result += twoPi;
  return result;
}

export function addVectors(a: Vector3Data, b: Vector3Data): Vector3Data {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function subtractVectors(a: Vector3Data, b: Vector3Data): Vector3Data {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function scaleVector(v: Vector3Data, s: number): Vector3Data {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

export function vectorLength(v: Vector3Data): number {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}

export function zeroVector(): Vector3Data {
  return { x: 0, y: 0, z: 0 };
}

/**
 * Resolve E na equação de Kepler M = E - e sin(E) via Newton-Raphson.
 */
export function solveEccentricAnomaly(
  meanAnomalyRad: number,
  eccentricity: number,
  tolerance = 1e-10,
  maxIterations = 50,
): number {
  const M = normalizeAngleRad(meanAnomalyRad);
  let E = eccentricity < 0.8 ? M : Math.PI;

  for (let index = 0; index < maxIterations; index += 1) {
    const f = E - eccentricity * Math.sin(E) - M;
    const derivative = 1 - eccentricity * Math.cos(E);
    const delta = f / derivative;
    E -= delta;
    if (Math.abs(delta) < tolerance) break;
  }

  return normalizeAngleRad(E);
}
